/**
 * 统一的持久化提供商接口
 * 定义所有提供商必须实现的方法
 */

export class PersistenceProvider {
  /**
   * 用户注册
   * @param {string} email - 用户邮箱
   * @param {string} password - 用户密码
   * @param {object} metadata - 用户元数据（如用户名）
   * @returns {Promise<{user: object, error: object}>}
   */
  async signUp(email, password, metadata = {}) {
    throw new Error('signUp not implemented')
  }

  /**
   * 用户登录
   * @param {string} email - 用户邮箱
   * @param {string} password - 用户密码
   * @returns {Promise<{user: object, error: object}>}
   */
  async signIn(email, password) {
    throw new Error('signIn not implemented')
  }

  /**
   * 用户登出
   * @returns {Promise<{error: object}>}
   */
  async signOut() {
    throw new Error('signOut not implemented')
  }

  /**
   * 获取当前会话
   * @returns {Promise<{data: {session: object}, error: object}>}
   */
  async getSession() {
    throw new Error('getSession not implemented')
  }

  /**
   * 获取当前用户
   * @returns {Promise<{data: {user: object}, error: object}>}
   */
  async getUser() {
    throw new Error('getUser not implemented')
  }

  /**
   * 监听认证状态变化
   * @param {function} callback - 回调函数(event, session)
   * @returns {object} 包含 unsubscribe 方法的订阅对象
   */
  onAuthStateChange(callback) {
    throw new Error('onAuthStateChange not implemented')
  }

  /**
   * 确保用户配置文件存在
   * @param {object} user - 用户对象
   * @param {string} username - 用户名
   * @returns {Promise<void>}
   */
  async ensureUserProfile(user, username) {
    throw new Error('ensureUserProfile not implemented')
  }

  /**
   * 从数据库表查询数据
   * @param {string} collection - 表/集合名称
   * @returns {object} 查询构建器对象
   */
  from(collection) {
    throw new Error('from not implemented')
  }

  /**
   * 获取数据库实例
   * @returns {object} 数据库实例
   */
  getDatabase() {
    throw new Error('getDatabase not implemented')
  }
}

/**
 * 查询构建器接口
 */
export class QueryBuilder {
  /**
   * 选择字段
   * @param {string} fields - 字段列表 ('*' 表示所有字段)
   * @returns {QueryBuilder}
   */
  select(fields = '*') {
    throw new Error('select not implemented')
  }

  /**
   * 插入数据
   * @param {object|array} data - 要插入的数据
   * @returns {Promise<{data: object, error: object}>}
   */
  async insert(data) {
    throw new Error('insert not implemented')
  }

  /**
   * 更新数据
   * @param {object} data - 要更新的数据
   * @returns {QueryBuilder}
   */
  update(data) {
    throw new Error('update not implemented')
  }

  /**
   * 删除数据
   * @returns {Promise<{error: object}>}
   */
  async delete() {
    throw new Error('delete not implemented')
  }

  /**
   * 相等条件
   * @param {string} column - 列名
   * @param {any} value - 值
   * @returns {QueryBuilder}
   */
  eq(column, value) {
    throw new Error('eq not implemented')
  }

  /**
   * IN 条件
   * @param {string} column - 列名
   * @param {array} values - 值数组
   * @returns {QueryBuilder}
   */
  in(column, values) {
    throw new Error('in not implemented')
  }

  /**
   * 排序
   * @param {string} column - 列名
   * @param {object} options - 选项 {ascending: boolean}
   * @returns {QueryBuilder}
   */
  order(column, options = {}) {
    throw new Error('order not implemented')
  }

  /**
   * 返回单个结果
   * @returns {Promise<{data: object, error: object}>}
   */
  async single() {
    throw new Error('single not implemented')
  }

  /**
   * 执行查询
   * @returns {Promise<{data: array, error: object}>}
   */
  async exec() {
    throw new Error('exec not implemented')
  }
}
